import {
  CpRweRight,
  GroupRweRight,
  RoleCpRightDto,
  RoleGroupCpRightDto,
  UserCpRightDto,
  UserGroupCpRightDto,
} from 'entities/charge-point-rights';
import { Group, GroupDto } from 'entities/group';

import { LowerCaseRight } from '../../models/identity';
import { CP_PREFIX, DELIMETER, GROUP_PREFIX } from './constants';
import { ChargePointRightTableItem } from './types';

export const insertIntoTable = ({
  existingRows,
  subRowsToInsert,
  path,
}: {
  existingRows: ChargePointRightTableItem[];
  subRowsToInsert: ChargePointRightTableItem[];
  path: string[];
}): ChargePointRightTableItem[] => {
  const id = path[0];

  const isBaseCase = path.length === 1;

  if (isBaseCase) {
    return existingRows.map((row, index) => {
      const isMatchedRowWithoutSubRows = index === Number(id) && !row.subRows;

      if (isMatchedRowWithoutSubRows) {
        return {
          ...row,
          subRows: subRowsToInsert,
        };
      }

      return row;
    });
  }

  return existingRows.map((row, index) => {
    const isMatchedRowWithSubRows = index === Number(id) && row.subRows;

    if (isMatchedRowWithSubRows) {
      const [, ...updatedPath] = path;

      return {
        ...row,
        subRows: insertIntoTable({
          existingRows: row.subRows,
          subRowsToInsert,
          path: updatedPath,
        }),
      };
    }

    return row;
  });
};

export const recursivelyUpdateTable = ({
  tableData,
  childData,
  id,
}: {
  tableData: ChargePointRightTableItem[];
  childData: ChargePointRightTableItem[];
  id: string;
}) => {
  return insertIntoTable({
    existingRows: tableData,
    subRowsToInsert: childData,
    path: id.split('.'),
  });
};

export const recursivelyAddSubgroups = (
  groups: Group[],
  groupChargePointRights: UserGroupCpRightDto[] | RoleGroupCpRightDto[]
) => {
  const result: ChargePointRightTableItem[] = [];

  groups.forEach((group) => {
    const { id, name, childrenGroups } = group;

    const groupRight = groupChargePointRights.find(
      (right) => right.groupName === name
    ) as UserGroupCpRightDto | RoleGroupCpRightDto | undefined;

    if (childrenGroups.length === 0) {
      const row: ChargePointRightTableItem = {
        id: String(id),
        name,
        isGroup: true,
        read: false,
        write: false,
        execute: false,
      };

      if (groupRight) {
        const { read, write, execute } = groupRight;

        row.read = read;
        row.write = write;
        row.execute = execute;
      }

      return result.push(row);
    }

    return result.push({
      isGroup: true,
      id: String(id),
      name,
      subRows: recursivelyAddSubgroups(childrenGroups, groupChargePointRights),
      read: false,
      write: false,
      execute: false,
    });
  });

  return result;
};

export const getFormRightsArrays = (values: Record<string, boolean>) => {
  return Object.keys(values).reduce(
    (acc, el) => {
      const [prefix, id, type] = el.split(DELIMETER) as [
        string,
        string,
        LowerCaseRight
      ];

      const defaultRights = {
        read: false,
        write: false,
        execute: false,
      };

      const isGroupRight = prefix === GROUP_PREFIX;

      if (isGroupRight) {
        if (acc[0][id]) {
          acc[0][id][type] = values[el];
        } else {
          acc[0][id] = {
            ...defaultRights,
            groupName: id,
            [type]: values[el],
          };
        }
      } else {
        if (acc[1][id]) {
          acc[1][id][type] = values[el];
        } else {
          acc[1][id] = {
            ...defaultRights,
            chargePointId: id,
            [type]: values[el],
          };
        }
      }

      return acc;
    },
    [{}, {}] as [Record<string, GroupRweRight>, Record<string, CpRweRight>]
  );
};

export const getInitialValues = (
  chargePointRights: UserCpRightDto[] | RoleCpRightDto[],
  groupChargePointRights: UserGroupCpRightDto[] | RoleGroupCpRightDto[]
) => {
  const acc: Record<string, boolean> = {};

  chargePointRights.forEach((right) => {
    const { chargePointId, read, write, execute } = right;

    acc[`${CP_PREFIX}${DELIMETER}${chargePointId}${DELIMETER}read`] = read;
    acc[`${CP_PREFIX}${DELIMETER}${chargePointId}${DELIMETER}write`] = write;
    acc[`${CP_PREFIX}${DELIMETER}${chargePointId}${DELIMETER}execute`] =
      execute;
  });
  groupChargePointRights.forEach((right) => {
    const { groupName, read, write, execute } = right;

    acc[`${GROUP_PREFIX}${DELIMETER}${groupName}${DELIMETER}read`] = read;
    acc[`${GROUP_PREFIX}${DELIMETER}${groupName}${DELIMETER}write`] = write;
    acc[`${GROUP_PREFIX}${DELIMETER}${groupName}${DELIMETER}execute`] = execute;
  });

  return acc;
};

export const getGroupsRightsTableData = (
  groups: GroupDto[],
  groupChargePointRights: UserGroupCpRightDto[] | RoleGroupCpRightDto[]
) => {
  return groups.map(({ id, name, childrenGroups }) => {
    const row: ChargePointRightTableItem = {
      id: String(id),
      name,
      isGroup: true,
      read: false,
      write: false,
      execute: false,
    };

    const groupRight = groupChargePointRights.find(
      (right) => right.groupName === name
    );

    if (groupRight) {
      const { read, write, execute } = groupRight;

      row.read = read;
      row.write = write;
      row.execute = execute;
    }

    if (childrenGroups.length > 0) {
      row.subRows = recursivelyAddSubgroups(
        childrenGroups,
        groupChargePointRights
      );
    }

    return row;
  });
};
